/**
 * @typedef {Object} FigureLinksConfig
 * @property {boolean} enabled - if this link should be rendered
 * @property {string} [classList] - a class list to add at the link
 * @property {string} [text] - the link context
 * @property {string} [ariaLabel] - the content of the aria-label attribute
 * @property {string} [icon] - the link icon, it should map to an element at the icons object
 * @property {boolean} [prependIcon] - if the icon should be added before the link text
 **/
/**
 * @typedef {Object} FigureConfig
 * @property {boolean} addWrapper - if a wrapper should be added to the figure
 * @property {boolean} renderContent - if the content of the figure should be rendered
 * @property {boolean} renderCaption - if the caption of the figure should be rendered
 * @property {boolean} renderNotes - if the notes of the figure should be rendered
 * @property {boolean} renderTitle - if the title of the figure should be rendered
 * @property {FigureLinksConfig} linkOpenAllinViewer - configuration for the link to open all figures in the FV (directory)
 * @property {FigureLinksConfig} linkToContent - configuration for the link to go back to the original in the article content
 * @property {FigureLinksConfig} linkToViewer - configuration for the link to open the figure in the FV (lightbox)
 * @property {FigureLinksConfig} linkToViewerAlt - configuration for the link to open the figure in the FV (lightbox), the alternative option is for a link to wrap the figure content and open it for example from clicking the img
 * @property {boolean} [repositionCaptionNotes] - if the notes should be repositioned
 * @property {boolean} [openFromBodyLinks] - if the FV should be opened from links in the article content
 * @property {Object} [fillInMissingData] - configuration for the missing data of the figure
 * @property {boolean} fillInMissingData.captionEnabled - if the caption should be filled in case it is missing
 * @property {string} fillInMissingData.caption - the caption to be added in case it is missing
 * @property {boolean} fillInMissingData.titleEnabled - if the title should be filled in case it is missing
 * @property {string} fillInMissingData.title - the title to be added in case it is missing
 * @property {string} titleSelector - the selector to find the title of the figure
 **/
/**
 * @typedef {Object} FiguresConfig
 * @param {object} [selectors] - configuration for all figures
 * @param {FigureConfig} [graphic] - configuration for graphics (images)
 * @param {FigureConfig} [table] - configuration for tables
 * @param {FigureConfig} [media] - configuration for videos
 * @param {FigureConfig} [other] - configuration for all other unhandled cases
 * @param {FigureConfig} [all] - configuration applying to all media
 **/
/**
 * @typedef {Object} CollateralConfig
 * @param {FiguresConfig} figures - configuration for cloning the article figures
 **/
/**
 * @typedef {Object} FigureViewerConfig
 * @property {boolean} useLegacyUIViewer - if the legacy UI viewer should be used
 * @property {boolean} removeUnneededButtons - remove next/previous figure buttons... they will never be activated
 * @property {Object} info - configuration for the info panel
 * @property {Object} info.aside - configuration for the aside info panel
 * @property {boolean} info.aside.rendersOpen - if the aside info panel should be open by default
 * @property {boolean} info.aside.hasTitle - if the aside info panel should have a title
 * @property {string[]} info.aside.content - the content to be rendered in the aside info panel
 * @property {Object} info.toolbar - configuration for the toolbar info panel
 * @property {string} info.toolbar.top - the top content of the toolbar
 * @property {string} info.toolbar.bottom - the bottom content of the toolbar
 * @property {string} info.toolbar.selector - the selector of the toolbar
 * @property {Object} download - configuration for the download panel
 * @property {string[]} download.linkOption - what download type link will be rendered at the standalone link
 * @property {string[]} download.panelOptions - what download type links will be rendered at the panel
 * @property {string} download.preventFor - the selector for the figures to prevent downloading for
 * @property {Object} download.renameSourceExt - map to rename the source extension f.ex JPEG -> JPG
 * @property {Object} share - configuration for the share panel
 * @property {Object} share.s - configuration for the share panel selectors
 * @property {string} share.s.buttons - the buttons selector
 * @property {string} share.s.selector - the panel selector
 * @property {string} share.s.trigger - the trigger selector
 * @property {Object} s - configuration for the figure viewer selectors
 * @property {string} s.parent - the parent selector
 * @property {string} s.prefix - the prefix for the figure viewer
 * @property {string} s.trigger - the trigger selector
 * @property {string} s.panelTriggers - the panel triggers selector
 * @property {string} s.directory - the directory selector
 * @property {string} s.lightbox - the lightbox selector
 * @property {string} s.filmstrip - the filmstrip selector
 * @property {string} s.frame - the frame selector
 * @property {string} s.goBackToContent - the go back to content selector
 * @property {string} s.closeTriggers - the close triggers selector
 * @property {string} s.lockScreenClass - the lock screen class
 * @property {string} s.template - the template for the figure viewer
 * @property {string} s.slide - the slide selector
 * @property {string} s.toolbar - the toolbar selector
 * @property {string} s.carouselArrows - the carousel arrows selector
 * @property {Object} figure - configuration for the figure viewer figures
 * @property {FigureConfig} figure.graphic - configuration for the graphic figures
 * @property {FigureConfig} figure.table - configuration for the table figures
 * @property {FigureConfig} figure.media - configuration for the media figures
 * @property {Object} focusMode - configuration for the focusMode feature
 * @property {boolean} focusMode.enabled - if the focusMode should be enabled
 * @property {Object} tabs - configuration for the figure viewer tabs
 * @property {string} tabs.appendSelector - the selector to append the tabs
 * @property {string} tabs.buttonSelector - the button selector
 * @property {boolean} tabs.closeIfActive - if the tab should close if active
 * @property {boolean} tabs.enableArrowKeysTabs - if the arrow keys should be enabled for the tabs
 * @property {string} tabs.paneHeader - the pane header selector
 * @property {string} tabs.paneSelector - the pane selector
 * @property {boolean} tabs.triggerFirst - if the first tab should be triggered
 * @property {string} appendTo - the selector to append the figure viewer
 * @property {string} [appendTo] - the selector to append the figure viewer
 **/

/**
 * @param {boolean} enabled = true - if a Focus Trap will be activated
 * @param {object} [createOptions  = {}]- check documentation of the used library https://www.npmjs.com/package/focus-trap
 * @param {object} [initialFocus = {}] - custom set of the initialFocus element per container
 * @param {boolean} [isIframe = false] - if the targeted element is inside an iframe
 */
const focusTrapConfig = {
    enabled: true,
    createOptions: {
        allowOutsideClick: true,
        preventScroll: true,
    },
    initialFocus: {},
    isIframe: false,
};
/**
 * @param {CollateralConfig} collateral - configuration for all article figures
 * @param {FiguresConfig} figures - configuration for all article figures
 * @param {FigureViewerConfig} figureViewer - configuration for all article figures
 **/
const config = {
    articlePageHeader: {
        selector: 'header.header',
        boundingClientSelector: '[data-extent="frontmatter"] header, header[data-extent="frontmatter"], h1',
        boundingSpacer: 0,
        hideOnScroll: true,
        removeFixedStatus: false,
    },
    classNames: {
        ACTIVE: 'active',
        BTN: 'btn',
        BTN_INVERSE: 'btn--inverse',
        BTN_PRIMARY: 'btn-primary',
        DISABLED: 'disabled',
        DROPDOWN_MENU: 'dropdown__menu',
        FOCUSED: 'focused',
        FIXED: 'fixed',
        HIDDEN: 'hidden',
        JS_HIDDEN: 'js--hidden',
        JS_VISIBLE: 'js--visible',
        JOURNAL_BRANDING: 'journal-branding',
        MT3: 'mt-3',
        SHOW: 'show',
    },
    citations: {
        s: {
            item: '[role="doc-bibliography"] .citation',
            parent: '[role~="listitem"]',
            label: '.label',
            prefix: 'core-',
            linkWrapper: 'external-links',
            point: 'data-xml-rid~="',
        },
        pointTo: 'collateral', // 'body'
        multiRefPopup: {
            enabled: false,
            marginTop: 80,
            s: {
                parent: 'article[data-design]',
                wrapper: '.references-pop-up',
                close: '.references-pop-up__close',
                refNumber: '.references-pop-up__heading-number',
                refList: '.external-links',
                citationList: '.to-citation__accordion',
                citationTarget: '.to-citation__accordion .to-citation',
                citationPopupTarget: '.external-links .to-citation',
            },
        },
        renderAsDropBlocks: false,
    },
    collapsible: {
        enabled: false, // TODO is this used?
        collateralEnabled: false,
        textKeys: ['citations', 'coreProducts'],
        figureKeys: [], // 'graphic', 'table'
        citations: {
            method: 'clamp',
            value: 4,
            parent: '[data-extent="backmatter"] [role="doc-bibliography"]',
            wrapper: '[role="list"]',
        },
        coreProducts: {
            method: 'clamp',
            value: 6,
            parent: '[data-extent="backmatter"] .core-product',
            wrapper: '[role="list"]',
        },
        graphic: {
            conditional: true,
            height: '400px',
            wrap: '.figure-wrap',
            scrollParentIntoView: true,
        },
        table: {
            conditional: true,
            height: '280px',
            wrap: '.table-wrap',
        },
    },
    collateral: {
        affiliationsTitle: undefined,
        citedBySelector: '.cited-by__entry',
        disable: false,
        hideDisabled: true,
        retainSessionStatus: true,
        navMargin: {
            left: 80,
            top: 80,
            right: 16,
        },
        peerReviewHistory: {
            selector: '.core-review-history a',
            innerHTML: '<i class="icon-PDF"></i><span>Download</span>',
            classList: `[this.s.BTN, this.s.MT3]`,
        },
        permissions: {
            identifier: '.core-permissions',
            linkSelector: '.btn',
            removeCondition: null,
        },
        removePermissions: false,
        s: {
            main: '.core-collateral',
            menu: '[data-core-nav="collateral"]',
            prefix: 'core-collateral',
            tabPrefix: 'tab-',
            paneIds: {
                info: 'core-collateral-info',
                information: 'tab-information',
                contributors: 'tab-contributors',
                metrics: 'core-collateral-metrics',
                metricsInner: 'tab-metrics-inner',
                citations: 'tab-citations',
                fulltextOptions: 'core-collateral-fulltext-options',
                purchaseAccess: 'core-collateral-purchase-access',
                references: 'core-collateral-references',
                figures: 'core-collateral-figures',
                tables: 'core-collateral-tables',
                media: 'core-collateral-media',
                more: 'core-collateral-more',
                selfCitation: 'core-collateral-self-citation',
                share: 'core-collateral-share',
            },
            citations: '[data-extent="backmatter"] [role="doc-bibliography"] [role="list"]',
            item: '#core-collateral-references .citation',
            linkWrapper: 'external-links',
            wrapper: 'section--wrapper',
        },
        share: {
            delay: 3000,
            selectors: {
                action: '.share-self__action',
                source: '.share-self__source',
                status: '.share-self__status',
                terms: '#terms-checkbox',
                wrapper: '#share-self',
            },
        },
        tooltips: false,
        figures: {
            graphic: {
                renderContent: true,
                linkOpenAllinViewer: {
                    enabled: true,
                },
            },
            table: {
                linkToContent: {
                    text: 'gototable',
                },
                linkOpenAllinViewer: {
                    enabled: true,
                },
            },
            media: {
                linkToContent: {
                    text: 'gotomedia',
                },
            },
            all: {
                linkToContent: {
                    enabled: true,
                },
                linkToViewer: {
                    icon: undefined,
                    classList: undefined,
                    text: 'opengeneric',
                    prependIcon: false,
                },
                addWrapper: true,
                renderContent: false,
                renderCaption: true,
                renderNotes: false,
                renderTitle: false,
            },
        },
    },
    contributors: {
        byline: {
            CONTRIBUTORS: '[data-extent="frontmatter"] .contributors, article header .contributors',
            DISPLAYED: 'data-displayed-on',
            ON_DISPLAY: 'data-on-display',
            HIDDEN: 'data-hidden-on',
            TOGGLER: 'data-action',
        },
        expandOnFocus: true,
        selectors: {
            CONTENT: '.content',
            LIST: '[property="author"], [property="editor"], [property="reviewer"]',
            TRIGGER: '.heading',
            TOGGLE_ALL: '.collateral-contributors-control',
            TOGGLE_ALL_WRAPPER: '.core-authors h4, .core-authors .expand-all-wrapper',
            TOGGLE_TEMPLATE: 'collapsible_authors_template',
        },
        settings: {
            TOGGLE_ALL_POSITION: 'append',
            WITH_CHEVRON: true,
            WITH_TOGGLE_ALL: true,
        },
    },
    dropdown: {
        wrapper: 'article[data-design]',
        selector: '[data-trigger="dropdown"]',
        closeOnOutsideClick: false,
        focusNext: false,
    },
    enhancements: {
        bodyPrefix: 'body-ref-',
        citationSnippetLength: 44,
        enumeration: [
            'a',
            'b',
            'c',
            'd',
            'e',
            'f',
            'g',
            'h',
            'i',
            'j',
            'k',
            'l',
            'm',
            'n',
            'o',
            'p',
            'q',
            'r',
            's',
            't',
            'u',
            'v',
            'w',
            'x',
            'y',
            'z',
        ],
        prefix: 'core-',
        suffix: '-core',
        addDataIds: false,
        dataIds: {
            authors_view_all: 'article-authors-viewall',
            info: 'article-pill-info',
            information: 'article-collateral-tab-info',
            contributors: 'article-collateral-tab-contributors',
            metrics: 'article-pill-metrics',
            metricsInner: 'article-collateral-tab-metrics',
            citations: 'article-collateral-tab-citations',
            fulltextOptions: 'article-pill-fulltextOptions',
            purchaseAccess: 'article-pill-purchaseAccess',
            references: 'article-pill-references',
            media: 'article-pill-media',
            mediaFigures: 'article-collateral-tab-figures',
            mediaTables: 'article-pill-tables',
            mediaOther: 'article-collateral-tab-other',
            more: 'article-pill-more',
            selfCitation: 'article-nav-menubar-citations',
            share: 'article-pill-share',
        },
        enableArrowKeysTabs: true,
        enhanceIframeAccessibility: true,
    },
    figures: {
        selectors: {
            figure: 'figure',
            table: 'table',
            grouped: {
                attribute: 'role',
                value: 'group',
            },
            caption: '.caption',
            notes: '.notes',
            parentSelector: '.figure-wrap',
        },
        graphic: {
            class: 'graphic',
            contentSelector: ':scope > img, :scope > .graphic-wrap > img',
        },
        table: {
            class: 'table',
            contentSelector: '.table-wrap, :scope > img',
            fillInMissingData: {
                title: 'Unlabeled table',
            },
            smartTables: {
                classes: {
                    filter_table: 'core-filterable',
                    filter_tr: 'filtering',
                    filter_active: 'filtered',
                    even: 'even',
                    odd: 'odd',
                    sort_table: 'core-sortable',
                },
                enabled: false,
                enableFor: '[data-type="datasheet"], [data-type="technical-factsheet"]',
                filter: true,
                rowLimit: 10,
                sort: true,
            },
            linkToViewerAlt: {
                enabled: false,
            },
        },
        media: {
            class: 'media',
            contentSelector: 'iframe, video',
            customSelector: '.core-embedded-content',
            fillInMissingData: {
                title: 'Unlabeled media',
            },
            linkToViewer: {
                enabled: false,
            },
            linkToViewerAlt: {
                enabled: false,
            },
        },
        other: {},
        all: {
            fillInMissingData: {
                enabled: true,
                title: 'Unlabeled figure',
            },
            linkToContent: {
                enabled: false,
                classList: 'pill__to-original',
                ariaLabel: 'goto',
                text: 'gotographic',
                icon: 'ICON_RETURN',
                prependIcon: true,
            },
            linkOpenAllinViewer: {
                enabled: false,
                classList: 'btn btn--inverse',
                text: 'openall',
                icon: 'ICON_ALL_IN_VIEWER',
            },
            linkToViewer: {
                enabled: true,
                ariaLabel: 'openspecific',
                text: 'opengeneric', // change to 'remove' for no text
                icon: 'ICON_ALL_IN_VIEWER',
                classList: 'btn btn-dark figure-pop-btn', // remove figure-pop-btn
                prependIcon: true,
                customWrapper: ':scope > header',
            },
            linkToViewerAlt: {
                enabled: true,
                ariaLabel: 'openspecific',
            },
            preventFor: {
                attribute: 'data-position',
                enabled: false,
                values: 'anchor',
            },
            titleSelector: 'header .label, :scope > figcaption .heading, :scope > figure > figcaption .heading',
        },
        focusOnWrapper: true,
    },
    figureViewer: {
        enabled: true,
        useLegacyUIViewer: false,
        info: {
            aside: {
                rendersOpen: true,
                hasTitle: false,
                content: ['unhandledData', 'caption', 'notes'], // 'title'
                s: {
                    title: '#core-fv__info .core-fv__panel__title', // #core-fv__info .core-fv__panel__header__title
                    text: '#core-fv__info .core-fv__panel__text',
                },
            },
            toolbar: {
                top: 'caption',
                // bottom: 'title',
                selector: '#core-fv__toolbar__info',
            },
        },
        download: {
            linkOption: 'img',
            panelOptions: ['img', 'ppt', 'srcData'],
            preventFor: '[data-specific-use="do-not-distribute"]',
            renameSourceExt: {},
            selector: '#core-fv__download .core-fv__panel__text',
        },
        share: {
            s: {
                buttons: '.share-buttons',
                selector: '#core-fv__share .core-fv__panel__text',
                trigger: '[aria-controls="core-fv__share"]',
            },
        },
        zoom: {
            s: {
                zoomTrigger: '.core-fv__zoom.core-fv__btn',
            },
            options: {
                initialZoom: 150,
                hasZoomButtons: true,
            },
        },
        s: {
            parent: "this.articleView.getArticle() || document.body.querySelector('main') || document.body",
            prefix: 'core-fv',
            panelTriggers: '.core-fv__btn[aria-controls]',
            directory: '.core-fv__directory',
            lightbox: '.core-fv__lightbox',
            filmstrip: '.core-fv__filmstrip',
            frame: '.figures-group-frame',
            goBackToContent: '.core-fv__back-to-original',
            closeTriggers: '.core-fv__close',
            lockScreenClass: 'core-fv-lock-screen',
            template: 'fv_skeleton_template',
            slide: '.slide',
            holster: '.core-fv__toolbar__holster',
            toolbar: '.core-fv__toolbar',
            infoTrigger: '.core-fv__info',
            panelText: '.core-fv__panel__text',
            carousel: '.core-fv-carousel',
            carouselArrows: '.core-fv-carousel_control',
        },
        figure: {
            graphic: {
                openFromBodyLinks: false,
            },
            table: {
                repositionCaptionNotes: true,
                openFromBodyLinks: false,
            },
            media: {},
        },
        focusMode: {
            enabled: false,
        },
        tabs: {
            appendSelector: undefined,
            buttonSelector: '[role="tab"]',
            closeIfActive: false,
            enableArrowKeysTabs: true,
            paneHeader: undefined,
            paneSelector: '[role="tabpanel"]',
            triggerFirst: true,
        },
        removeUnusedElements: {
            carouselControls: true,
            graphicTab: false,
            mediaTab: false,
            tableTab: false,
        },
    },
    focusTrap: {
        collateral: focusTrapConfig,
        crossmark: focusTrapConfig,
        figureViewer: focusTrapConfig,
    },
    footnotes: {
        s: {
            item: '#footnotes [role="doc-footnote"] > [role="paragraph"], .core-footnote-group [role="doc-footnote"]',
            parent: '[role~="doc-footnote"]',
            label: '.label',
            prefix: 'core-',
            linkWrapper: 'external-links',
            point: 'href="#',
        },
        pointTo: 'body',
    },
    getFtr: {
        enabled: false,
        selectors: {
            body: {
                external: '#bibliography .core-xlink-crossref:has(a[href^="http"])',
                internal: '#bibliography .core-xlink-crossref:has(a[href^="/"])',
            },
            collateral: {
                external: '#core-collateral-references .core-xlink-crossref:has(a[href^="http"]',
                internal: '#core-collateral-references .core-xlink-crossref:has(a[href^="/"])',
            },
        },
    },
    icons: {
        ICON_ACCORDION_ARROW: 'icon-arrow_up',
        ICON_ACCORDION_COLLAPSED: undefined,
        ICON_ACCORDION_EXPANDED: undefined,
        ICON_ALL_IN_VIEWER: 'icon-open_in_new',
        ICON_IN_VIEWER_ALT: 'icon-fullscreen',
        ICON_BACK: 'icon-arrow_left',
        ICON_CLOSE: 'icon-close',
        ICON_RETURN: 'icon-subdirectory_arrow_left',
        ICON_PLUS: 'icon-plus',
    },
    mobileBreakpoint: 769,
    pill: {
        scrollIndicator: true,
        containIntoView: true,
        referencesCount: {
            enabled: false,
            maxCount: 150,
        },
    },
    prismJs: {
        enabled: false,
    },
    removeFigcaptionHeading: false,
    selectors: {
        article: 'article[data-design]',
        title: 'h1[property="name"]',
        sections: '[data-core-nav="article"] a',
        dataScriptId: 'axel-publication-metadata',
        navigation: '.content-navigation',
        access: '[data-article-access]',
        accessType: '[data-article-access-type]',
        decorationsXReference: '#frontmatter header .core-container, header[data-extent="frontmatter"] .core-container',
    },
    sectionsNavbar: {
        animateMenu: true,
        containIntoView: true,
        id: '#article_sections_menu',
        trigger: '[aria-controls="article_sections_menu"]',
        scrollIndicator: true,
        markBackmatterStart: false,
        enableAutoScrollMenu: true,
        observer: {
            customRootMargin: '0px',
            hasDynamicRootMargin: false,
            options: {
                threshold: 0,
            },
        },
        overlappingPivot: null,
        dynamicSections: {
            enabled: false,
            list: [],
            wrapped: {
                enabled: false,
                wrappersSelectors: '',
            },
            insertionSectionsSelector:
                '[data-extent="frontmatter"] section[id], [data-extent="bodymatter"] section[id], [data-extent="backmatter"] section[id]',
        },
        collapseSections: {
            enabled: false,
            icon: 'icon-arrow_down',
        },
    },
    smartTables: {
        classes: {
            filter_table: 'core-filterable',
            filter_tr: 'filtering',
            filter_active: 'filtered',
            even: 'even',
            odd: 'odd',
            sort_table: 'core-sortable',
        },
        enabled: false,
        enableFor: '[data-type="datasheet"], [data-type="technical-factsheet"]',
        filter: true,
        rowLimit: 10,
        sort: true,
    },
    stickyHeader: {
        enable: true,
        breadcrumbs: true,
        identifyingAttributes: ['id', 'aria-labelledby'],
        reposition: true,
        repositionLocation: 'main',
        selectors: {
            alerts: '.st-header__alerts',
            articleBreadcrumbs: '.article__breadcrumbs',
            articleCitation: '.article-tools__citation',
            articleFavorite: '.article-tools__favorite',
            articleFormats: '.info-panel__formats',
            articleLogo: '.navbar__logo--light',
            citations: '.st-header__citation',
            cite: '.info-panel__citations',
            favorite: '.st-header__favorite',
            formats: '.st-header__formats',
            logo: '.st-header__logo',
            menu: '.header .journal-menu',
            navigation: '.st-header__nav',
            share: '.st-header__share',
            stickyHeader: '.st-header[role="navigation"]',
            title: '.st-header__title',
            titleLabel: '.st-header__label',
            titleSource: 'h1',
            tracker: '#progress-tracker',
        },
    },
    tabs: {
        abstracts: {
            appendSelector: undefined,
            buttonSelector: undefined,
            closeIfActive: false,
            enableArrowKeysTabs: true,
            id: 'abstracts',
            paneHeader: 'h3',
            paneSelector: '[role="doc-abstract"]',
            triggerFirst: true,
        },
        articleRelations: {
            appendSelector: undefined,
            buttonSelector: '.btn--article-relation',
            closeIfActive: true,
            enableArrowKeysTabs: false,
            paneHeader: undefined,
            paneSelector: '.core-relations .relationsGroup',
            triggerFirst: false,
        },
        collateral: {
            appendSelector: 'header',
            buttonSelector: '[role="tablist"] > button',
            closeIfActive: false,
            enableArrowKeysTabs: true,
            paneHeader: 'h3',
            paneSelector: ':scope > [role="tabpanel"]',
            triggerFirst: true,
        },
        figureViewer: {
            appendSelector: undefined,
            buttonSelector: '.tab__nav__item',
            closeIfActive: false,
            enableArrowKeysTabs: true,
            paneHeader: undefined,
            paneSelector: '.tab__pane',
            triggerFirst: true,
        },
    },
    textHighlighting: {
        enable: false,
    },
    topOffset: 0,
    mathjax: {
        config: {
            loader: {
                load: ['input/tex-base', 'input/mml', 'output/chtml', 'ui/menu'],
            },
            chtml: {
                matchFontHeight: false,
            },
            svg: {
                matchFontHeight: false,
            },
        },
        src: 'https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-mml-chtml.js',
    },
    videoPlayer: {
        brightcove: {
            selector: 'video-js',
            src: 'https://players.brightcove.net',
        },
        cadmore: {
            configuratorId: '',
            selector: 'iframe',
            src: 'https://play.cadmore.media/Player/',
            iframeClass: '',
            wrapperDivClass: '',
        },
        kaltura: {
            selector: '.kaltura-video',
            src: 'https://cdnapisec.kaltura.com',
        },
        default: {
            selector: 'video, iframe',
        },
    },
};

export default config;
